import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Activity, TickSquare } from 'react-iconly';
import emailjs from '@emailjs/browser';
import CTA from '../components/CTA';
import Testimonials from '../components/Testimonials';
import { devError, devWarn } from '../utils/devLog';

import AnimatedButton from '../components/AnimatedButton';
import ScrollVideoPlayer from '../components/ScrollVideoPlayer';
import { useLanguage } from '../context/LanguageContext';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
  devWarn('[Contact] EmailJS is not configured. Set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY in your .env file.');
}

// Left empty → the section renders as a static showcase image (no fake button).

const Contact = () => {
  const { t } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errors, setErrors] = useState({});
  const [videoOpen, setVideoOpen] = useState(false);
  const formRef = useRef(null);
  const formLoadedAt = useRef(Date.now());

  // Close the video lightbox on Escape
  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setVideoOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [videoOpen]);

  const formatPhoneNumber = (value) => {
    const cleaned = ('' + value).replace(/(?!^\+)[^\d]/g, '');
    const match = cleaned.match(/^(\+\d{2,3})?(\d{0,3})?(\d{0,4})?(\d{0,4})?/);
    if (match) {
      let formatted = '';
      if (match[1]) formatted += match[1];
      if (match[2]) formatted += (match[1] ? ' ' : '') + match[2];
      if (match[3]) formatted += (match[2].length === 3 ? ' ' : '') + match[3];
      if (match[4]) formatted += (match[3].length === 4 ? ' ' : '') + match[4];
      return formatted.trim();
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (formData.phone.replace(/[^\d]/g, '').length < 10) newErrors.phone = 'Phone number must be at least 10 digits';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (formData.website && !/^((https?:\/\/)?)[^\s.]+\.[^\s]+/.test(formData.website)) newErrors.website = 'Invalid website URL';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Anti-spam: a hidden honeypot field + a minimum fill time. Bots tend to
    // fill every field and submit instantly. Pretend success so they don't adapt.
    const honeypot = formRef.current?.elements?.botField?.value;
    const elapsed = Date.now() - formLoadedAt.current;
    if (honeypot || elapsed < 1500) {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', website: '' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      devError('[Contact] EmailJS not configured. Message was not sent.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        title: `Project Inquiry from ${formData.company}`,
        time: new Date().toLocaleString(),
        message: `Phone: ${formData.phone}\nCompany: ${formData.company}\nWebsite: ${formData.website}`
      };

      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', website: '' });
    } catch (error) {
      devError('Email send failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    { question: t('contact.faq1Question'), answer: t('contact.faq1Answer') },
    { question: t('contact.faq2Question'), answer: t('contact.faq2Answer') },
    { question: t('contact.faq3Question'), answer: t('contact.faq3Answer') },
    { question: t('contact.faq4Question'), answer: t('contact.faq4Answer') },
    { question: t('contact.faq5Question'), answer: t('contact.faq5Answer') }
  ];

  return (
    <div id="contact" style={{ backgroundColor: 'var(--v2-color-offwhite)' }}>
      {/* 1. Form Section */}
      <section className="v2-container" style={{ display: 'flex', flexWrap: 'wrap', minHeight: '100vh', paddingTop: '120px', paddingBottom: '120px', gap: '40px' }}>
        {/* Left Column */}
        <div style={{ flex: '1 1 400px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ maxWidth: '480px' }}>
            <span style={{ marginBottom: '24px', backgroundColor: '#F3EFE9', color: '#1A1A1A', padding: '4px 16px 4px 4px', borderRadius: '24px', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ backgroundColor: '#FF7722', color: 'rgb(255, 255, 255)', borderRadius: '20px', padding: '4px 8px', fontSize: '11px', fontFamily: 'Youth, sans-serif', fontWeight: 500, lineHeight: '20px' }}>
                #1
              </span>
              {t('contact.availableForProjects')}
            </span>
            <h1 style={{ fontSize: '57px', fontFamily: 'Youth, sans-serif', fontWeight: 700, color: 'rgb(23, 20, 18)', letterSpacing: '-0.02em', lineHeight: '51px', marginBottom: '24px' }}>
              {t('contact.heading')}
            </h1>
            <p style={{ fontFamily: "var(--v2-font-body)", fontWeight: 400, color: 'rgb(23, 20, 18)', fontSize: '18px', lineHeight: '21px', marginBottom: '64px' }}>
              {t('contact.headingDesc')}
            </p>
            <div style={{ fontFamily: '"IBM Plex Mono", sans-serif', fontWeight: 400, color: 'rgb(142, 130, 124)', fontSize: '10px', lineHeight: '10px', marginBottom: '16px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {t('contact.trustedBy')}
            </div>
            <div style={{ display: 'flex', gap: '24px', opacity: 0.6, alignItems: 'center', flexWrap: 'wrap', fontWeight: 'bold', fontSize: '18px' }}>
              <span style={{ letterSpacing: '-1px' }}>{t('contact.startups')}</span> <span>{t('contact.smes')}</span> <span>{t('contact.enterprises')}</span> <span>{t('contact.agencies')}</span>
            </div>
          </div>
        </div>

        {/* Right Column (Form Container) */}
        <div style={{ flex: '1 1 500px', background: 'linear-gradient(to bottom, #FF4500 0%, #FF8C00 30%, var(--v2-color-offwhite) 80%)', borderRadius: '40px', padding: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden', fontFamily: "var(--v2-font-body)" }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '30%', height: '2px', backgroundColor: '#3322AA' }} />
            
            <h2 style={{ fontSize: '24px', fontFamily: 'Youth, sans-serif', letterSpacing: '-0.02em', marginBottom: '24px', lineHeight: 1.2 }}>{t('contact.formTitle')}</h2>
            
            <div style={{ backgroundColor: '#E8F5E9', color: '#2E7D32', padding: '12px 16px', borderRadius: '8px', marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: 600, fontSize: '14px' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#4CAF50"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              {t('contact.availableSlots')}
            </div>

            <form ref={formRef} onSubmit={handleSubmit}>
              {/* Honeypot — invisible to humans, traps bots. Keep this field. */}
              <input
                type="text"
                name="botField"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
              />
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--v2-color-charcoal)' }}>{t('contact.fullName')}</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px 16px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }} 
                  placeholder="John Doe" 
                  required
                />
                {errors.name && <span style={{ color: '#D32F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.name}</span>}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--v2-color-charcoal)' }}>{t('contact.emailRequired')}</label>
                <div style={{ display: 'flex', border: '1px solid #E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', backgroundColor: '#F9F9F9', borderRight: '1px solid #E0E0E0' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </div>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{ flex: 1, border: 'none', padding: '12px 16px', outline: 'none', fontSize: '16px', width: '100%', boxSizing: 'border-box' }} 
                    placeholder="john@doe.com" 
                    required
                  />
                </div>
                {errors.email && <span style={{ color: '#D32F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--v2-color-charcoal)' }}>{t('contact.phoneRequired')}</label>
                <div style={{ display: 'flex', border: '1px solid #E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', backgroundColor: '#F9F9F9', borderRight: '1px solid #E0E0E0', cursor: 'pointer' }}>
                    <span style={{ fontSize: '18px', marginRight: '4px' }}>🇮🇩</span>
                    <ChevronDown size={14} set="curved" weight="bold" />
                  </div>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{ flex: 1, border: 'none', padding: '12px 16px', outline: 'none', fontSize: '16px', width: '100%', boxSizing: 'border-box' }} 
                    placeholder="+62 812 3456 7890" 
                    required
                  />
                </div>
                {errors.phone && <span style={{ color: '#D32F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--v2-color-charcoal)' }}>{t('contact.companyRequired')}</label>
                <input 
                  type="text" 
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '12px 16px', border: '1px solid #E0E0E0', borderRadius: '4px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }} 
                  required
                />
                {errors.company && <span style={{ color: '#D32F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.company}</span>}
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--v2-color-charcoal)' }}>{t('contact.websiteRequired')}</label>
                <div style={{ display: 'flex', border: '1px solid #E0E0E0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', backgroundColor: '#F9F9F9', borderRight: '1px solid #E0E0E0' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                  </div>
                  <input 
                    type="url" 
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    style={{ flex: 1, border: 'none', padding: '12px 16px', outline: 'none', fontSize: '16px', width: '100%', boxSizing: 'border-box' }} 
                    placeholder="www.doe.com" 
                    required
                  />
                </div>
                {errors.website && <span style={{ color: '#D32F2F', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.website}</span>}
              </div>
              
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div style={{ backgroundColor: '#E8F5E9', color: '#2E7D32', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px' }}>
                   <TickSquare size={20} set="curved" weight="bold" /> {t('contact.successMsg')}
                </div>
              )}
              {submitStatus === 'error' && (
                <div style={{ backgroundColor: '#FFEBEE', color: '#C62828', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px' }}>
                  {t('contact.errorMsg')}
                </div>
              )}

              <AnimatedButton 
                type="submit" 
                disabled={isSubmitting}
                style={{ 
                  width: '100%', 
                  padding: '16px', 
                  borderRadius: '6px', 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                }}
                bgColor={isSubmitting ? '#ccc' : '#FF6D00'}
                textColor="white"
                hoverBgColor={isSubmitting ? '#ccc' : '#E65100'}
                hoverTextColor="white"
                icon={isSubmitting ? <Activity size={20} className="animate-spin" set="curved" weight="bold" /> : null}
                rightIcon={!isSubmitting ? <ArrowRight size={20} set="curved" weight="bold" /> : null}
              >
                {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
              </AnimatedButton>

              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <AnimatedButton 
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/pricing';
                  }}
                  style={{ 
                    width: '100%', 
                    padding: '16px', 
                    borderRadius: '6px', 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    border: '1px solid var(--v2-color-charcoal)'
                  }}
                  bgColor="transparent"
                  textColor="var(--v2-color-charcoal)"
                  hoverBgColor="var(--v2-color-charcoal)"
                  hoverTextColor="white"
                >
                  {t('contact.loginPricing')}
                </AnimatedButton>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* 2. FAQ Section */}
      <section className="v2-container" style={{ paddingTop: '120px', paddingBottom: '120px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px' }}>
          <div style={{ flex: '1 1 300px' }}>
            <h2 style={{ fontSize: 'clamp(40px, 5vw, 64px)', fontFamily: 'Youth, sans-serif', letterSpacing: '-0.02em', lineHeight: 0.9 }}>{t('contact.faqTitle')}</h2>
          </div>
          <div style={{ flex: '2 1 500px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, index) => (
              <div key={index} style={{ backgroundColor: '#F6F4F0', borderRadius: '8px', padding: '24px', cursor: 'pointer', fontFamily: "var(--v2-font-body)" }} onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontFamily: 'Youth, sans-serif', fontWeight: 500 }}>{faq.question}</h3>
                   <ChevronDown size={20} style={{ transform: openFaq === index ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', color: 'var(--v2-color-taupe)' }} set="curved" weight="bold" />
                </div>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      style={{ overflow: 'hidden' }}
                    >
                      <p style={{ marginTop: '16px', color: 'var(--v2-color-taupe)', fontSize: '16px', lineHeight: 1.5 }}>
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Testimonials */}
      <Testimonials />

      {/* 4. Video Section */}
      <ScrollVideoPlayer 
        src="https://ixezilyqdbarjbqfoczc.supabase.co/storage/v1/object/public/assets/4K%20Beautiful%20Anime%20Scenery.mp4" 
        overlayText={t('contact.title')} 
      />

      {/* 5. CTA Section */}
      <CTA />

    </div>
  );
};

export default Contact;
